import { useQueryClient } from '@tanstack/react-query';

import { useDocumentUpload } from '@hooks';
import { AssessmentDomainDocumentDetail, UploadCallbacks } from '@libs/types';

interface UseAssessmentDocumentUploadParams {
  assessmentId: string;
  assessmentDomainId: string;
}

/**
 * Hook for managing assessment domain document uploads
 * Handles the full upload lifecycle: presigned URL creation, S3 upload, and confirmation
 *
 * @param assessmentId - The ID of the assessment
 * @param assessmentDomainId - The ID of the assessment domain
 * @returns Upload functions and state management utilities
 */
export const useAssessmentDocumentUpload = ({
  assessmentId,
  assessmentDomainId,
}: UseAssessmentDocumentUploadParams) => {
  const queryClient = useQueryClient();

  const baseHook = useDocumentUpload<AssessmentDomainDocumentDetail>({
    endpoints: {
      createUrl: `api/assessments/${assessmentId}/domains/${assessmentDomainId}/documents/create-upload-url`,
      confirm: (documentId) =>
        `api/assessments/${assessmentId}/domains/${assessmentDomainId}/documents/${documentId}/confirm-upload`,
      delete: (documentId) =>
        `api/assessments/${assessmentId}/domains/${assessmentDomainId}/documents/${documentId}`,
    },
    documentCategory: 'assessment',
  });

  /**
   * Upload a document to an assessment domain
   *
   * @param file - The file to upload
   * @param callbacks - Callbacks for upload lifecycle events (onStart, onProgress, onSuccess, onError)
   * @returns Upload ID for tracking and cancellation
   */
  const uploadDocument = async (file: File, callbacks: UploadCallbacks<AssessmentDomainDocumentDetail>) => {
    // Wrap the onSuccess callback to add cache invalidation
    const wrappedCallbacks: UploadCallbacks<AssessmentDomainDocumentDetail> = {
      ...callbacks,
      onSuccess: (document) => {
        // Invalidate relevant queries to trigger refetch
        // queryClient.invalidateQueries({ queryKey: ['assessments'] });
        queryClient.invalidateQueries({
          queryKey: ['assessments', assessmentId, 'domains', assessmentDomainId, 'documents'],
        });

        // Then call the original callback with the full document
        callbacks.onSuccess(document);
      },
    };

    return baseHook.uploadDocument(
      file,
      {}, // No additional metadata needed for assessment documents
      wrappedCallbacks,
    );
  };

  return {
    ...baseHook,
    uploadDocument,
  };
};
